/* CK | Custom Section: slider group. Adds arrows and dots to a scroll-snap track. */
if (!customElements.get('ck-slider')) {
  class CkSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ck-slider__track');
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.dots = this.querySelector('[data-slider-dots]');
      this.update = this.update.bind(this);

      this.track.addEventListener('scroll', this.update, { passive: true });
      this.prev?.addEventListener('click', () => this.scrollPage(-1));
      this.next?.addEventListener('click', () => this.scrollPage(1));

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.mutationObserver = new MutationObserver(() => this.observeItems());
      this.mutationObserver.observe(this.track, { childList: true });
      this.observeItems();

      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => {
          event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        });
      }
    }

    disconnectedCallback() {
      this.resizeObserver.disconnect();
      this.mutationObserver.disconnect();
    }

    get items() {
      return Array.from(this.track.children).filter((item) => item.offsetWidth > 0);
    }

    get gap() {
      return parseFloat(getComputedStyle(this.track).columnGap) || 0;
    }

    get pageWidth() {
      return this.track.clientWidth + this.gap;
    }

    get pages() {
      const first = this.items[0];

      if (!first) return 1;

      const perView = Math.max(1, Math.round(this.pageWidth / (first.offsetWidth + this.gap)));

      return Math.max(1, Math.ceil(this.items.length / perView));
    }

    observeItems() {
      this.items.forEach((item) => this.resizeObserver.observe(item));
      this.update();
    }

    scrollPage(direction) {
      this.track.scrollTo({ left: this.track.scrollLeft + direction * this.pageWidth, behavior: 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const overflows = max > 1;

      if (this.prev) this.prev.hidden = !overflows || this.track.scrollLeft <= 1;
      if (this.next) this.next.hidden = !overflows || this.track.scrollLeft >= max - 1;

      if (this.dots) this.updateDots(overflows, max);
    }

    updateDots(overflows, max) {
      const pages = overflows ? this.pages : 0;

      if (this.dots.children.length !== pages) {
        this.dots.replaceChildren(
          ...Array.from({ length: pages }, (_, index) => {
            const dot = document.createElement('button');

            dot.type = 'button';
            dot.className = 'ck-slider__dot';
            dot.setAttribute('aria-label', this.dataset.dotLabel.replace('__INDEX__', index + 1));
            dot.addEventListener('click', () => this.track.scrollTo({ left: index * this.pageWidth, behavior: 'smooth' }));

            return dot;
          })
        );
      }

      const atEnd = this.track.scrollLeft >= max - 1;
      const current = atEnd ? pages - 1 : Math.min(pages - 1, Math.round(this.track.scrollLeft / this.pageWidth));

      Array.from(this.dots.children).forEach((dot, index) => {
        dot.setAttribute('aria-current', index === current ? 'true' : 'false');
      });
    }
  }

  customElements.define('ck-slider', CkSlider);
}
